import * as THREE from 'three';

/**
 * Shared neutral materials, one palette per era. Player colour comes from the
 * `accent` and `glow` materials the layers pass in; everything else uses these.
 * Each era has its own palette object; restyling an era is editing its colours here.
 */

function flat(color: string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, flatShading: true });
}

/** The per-player emissive `glow` material (lights, visors, energy parts). */
export function glowMaterial(color: string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 1.2,
    flatShading: true,
  });
}

const TRANSLUCENT_OPACITY = 0.4;
const translucentCache = new WeakMap<THREE.Material, Map<number, THREE.Material>>();
const translucentSources = new WeakMap<
  THREE.Material,
  { source: THREE.Material; opacity: number }
>();

/** The material and opacity a `translucent` copy was made from (how a bake spots a see-through accent). */
export function translucentSource(
  material: THREE.Material
): { source: THREE.Material; opacity: number } | undefined {
  return translucentSources.get(material);
}

/**
 * A see-through copy of a player's `accent` or `glow` material (energy walls,
 * light fields, shield domes), made once per material and opacity and shared.
 */
export function translucent(
  material: THREE.Material,
  opacity = TRANSLUCENT_OPACITY
): THREE.Material {
  let byOpacity = translucentCache.get(material);
  if (!byOpacity) {
    byOpacity = new Map();
    translucentCache.set(material, byOpacity);
  }
  const cached = byOpacity.get(opacity);
  if (cached) return cached;
  const copy = material.clone();
  copy.transparent = true;
  copy.opacity = opacity;
  copy.depthWrite = false;
  copy.side = THREE.DoubleSide;
  byOpacity.set(opacity, copy);
  translucentSources.set(copy, { source: material, opacity });
  return copy;
}

/**
 * Healing light (the healer's orb, heal effects): the same green for every
 * player, so a heal never reads as anyone's team colour.
 */
export const HEAL_GLOW = new THREE.MeshStandardMaterial({
  color: '#8dffac',
  emissive: '#3ddc6a',
  emissiveIntensity: 1.4,
  flatShading: true,
});

export const CLASSICAL = {
  SKIN: flat('#e0b48a'),
  WOOD: flat('#7a5230'),
  DARK_WOOD: flat('#4a3220'),
  METAL: flat('#b8bcc4'),
  STRAW: flat('#d9b56a'),
  STONE: flat('#b9b2a3'),
  PLASTER: flat('#e8dcc0'),
  TERRACOTTA: flat('#a4553a'),
  SOIL: flat('#6b4a26'),
  WHEAT: flat('#e6bf45'),
  BARN: flat('#8b3a2b'),
  PINE: flat('#2f6b3a'),
  HORSE: flat('#8a5a36'),
  WALL_STONE: flat('#9d968a'),
  RIPE_WHEAT: flat('#d99a1e'),
  WATER: flat('#4f8fc9'),
  /** Bright running water (millraces), readable at game zoom. */
  RACE_WATER: flat('#8fd3f4'),
  WOOL: flat('#f2eee4'),
  HAY: flat('#b98a3e'),
  FOREST_PINE: flat('#1c4a21'),
  PLANK: flat('#c9a06a'),
  CABBAGE: flat('#6e9b3a'),
  /** The spy's cloak and hat: near-black, so its only colour is a small scarf. */
  CLOAK: flat('#2a2830'),
  FELT: flat('#1c1a1f'),
  /** The watchtower's telescope. */
  BRASS: flat('#c9a24a'),
};

export const INDUSTRIAL = {
  BRICK: flat('#9a4a36'),
  DARK_BRICK: flat('#6e3326'),
  IRON: flat('#4d5259'),
  STEEL: flat('#8a939c'),
  SOOT: flat('#2f3033'),
  SMOKE: new THREE.MeshStandardMaterial({
    color: '#cfd0d2',
    flatShading: true,
    transparent: true,
    opacity: 0.8,
  }),
  SLATE: flat('#5d6470'),
  ORE: flat('#6a5d52'),
  SPOIL: flat('#57504a'),
  RAIL: flat('#3b3d42'),
  SLEEPER: flat('#5b4330'),
  TRACTOR: flat('#c44b2b'),
  TYRE: flat('#26272a'),
  CLOCK: flat('#f1ead2'),
  COBBLE: flat('#8c877d'),
  SANDBAG: flat('#b8a47a'),
  /** Oil drums in the factory's vehicle yard. */
  DRUM: flat('#3f5b46'),
  /** The labourer's hard hat and his vest's reflective band. */
  HARD_HAT: flat('#f2c230'),
  REFLECTOR: flat('#e9ecef'),
  DENIM: flat('#3d5277'),
  SACK: flat('#c9ab76'),
};

export const MODERN = {
  CONCRETE: flat('#b7b6b0'),
  DARK_CONCRETE: flat('#86857f'),
  GLASS: new THREE.MeshStandardMaterial({
    color: '#6f9fbf',
    flatShading: true,
    metalness: 0.4,
    roughness: 0.2,
  }),
  ASPHALT: flat('#46484c'),
  ROAD_LINE: flat('#e8e2c8'),
  STEEL: flat('#a4adb6'),
  /** Warm window light, lit even in shade. */
  WINDOW: new THREE.MeshStandardMaterial({
    color: '#ffe3a1',
    emissive: '#ffcf6a',
    emissiveIntensity: 0.9,
    flatShading: true,
  }),
  WHITE: flat('#eeeeea'),
  RED_LIGHT: new THREE.MeshStandardMaterial({
    color: '#ff5a4a',
    emissive: '#ff3a2a',
    emissiveIntensity: 1.4,
    flatShading: true,
  }),
  GREEN_LEAF: flat('#5fae4a'),
  /** Military vehicles and uniforms. */
  OLIVE: flat('#5c6b3c'),
  DARK_OLIVE: flat('#3e4a2a'),
  GUNMETAL: flat('#3a3f45'),
  /** Tank tracks and tyres. */
  TRACK: flat('#232427'),
  /** Blast doors' hazard stripes. */
  HAZARD: flat('#e0b21f'),
  /** Radar dishes: white, seen from both sides. */
  DISH: new THREE.MeshStandardMaterial({
    color: '#eeeeea',
    flatShading: true,
    side: THREE.DoubleSide,
  }),
};

export const FUTURE = {
  HULL: flat('#e4e8ee'),
  PANEL: flat('#c3cad4'),
  DARK_PANEL: flat('#4a5360'),
  /** Translucent domes and glazing. */
  DOME: new THREE.MeshStandardMaterial({
    color: '#bfe3f2',
    flatShading: true,
    transparent: true,
    opacity: 0.55,
    metalness: 0.3,
    roughness: 0.1,
  }),
  PAD: flat('#9aa3ad'),
  GARDEN: flat('#4fa35a'),
  /** Soft ground shadow under hovering drones. */
  SHADOW: new THREE.MeshBasicMaterial({
    color: '#000000',
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
  }),
};
